import { plazosPago } from '../assets/data/arrays'

export interface CmdmPagoRow {
  plazo?: number | string
  valor?: number | string
  metodo_pago?: string
}

export interface CmdmCreditDisplayOrder {
  customer?: {
    first_name?: string
    last_name?: string
    document?: string
    tipo_documento?: string
  }
  billingAddress?: {
    company?: string
  }
  total?: string | number
  envio?: string | number
  extraDetails?: {
    plazo?: string | number
    payment_method?: string
    pagos?: CmdmPagoRow[]
  }
  pagos?: CmdmPagoRow[]
  payment_method?: string
}

export interface CmdmCreditDisplayInfo {
  empresa: string
  nit: string
  creditoOtorgado: string
  plazoCredito: string
}

export const formatCmdmNit = (document?: string | null): string => {
  const digits = (document ?? '').toString().replace(/\D/g, '')
  if (!digits) return '—'
  if (digits.length <= 10) {
    const base = digits.slice(0, Math.min(9, digits.length))
    const check = digits.length > 9 ? `-${digits.slice(9)}` : ''
    const grouped = base.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    return `${grouped}${check}`
  }
  return document?.trim() || digits
}

export const formatCreditoOtorgadoCop = (amount: number | null): string => {
  if (amount === null || Number.isNaN(amount) || amount <= 0) return 'No disponible'
  return `$${Math.round(amount).toLocaleString('es-CO')} COP`
}

export const getCmdmEmpresaDisplayName = (order: CmdmCreditDisplayOrder): string => {
  const company = order.billingAddress?.company?.trim()
  if (company) return company
  const first = order.customer?.first_name?.trim() ?? ''
  const last = order.customer?.last_name?.trim() ?? ''
  const full = `${first} ${last}`.trim()
  return full || '—'
}

/** Plazo en dias: prioridad fila en `pagos` (como en OrderPaymentInfo). */
export const getPlazoFromOrderPagos = (order: CmdmCreditDisplayOrder): string | number | null => {
  const fromExtra = order.extraDetails?.pagos
  const fromRoot = order.pagos
  const pagos =
    Array.isArray(fromExtra) && fromExtra.length > 0
      ? fromExtra
      : Array.isArray(fromRoot) && fromRoot.length > 0
        ? fromRoot
        : null
  if (pagos && pagos.length > 0) {
    const plazo = pagos[0]?.plazo
    if (plazo !== undefined && plazo !== null && plazo !== '') return plazo
  }
  const fallback = order.extraDetails?.plazo
  if (fallback !== undefined && fallback !== null && fallback !== '') return fallback
  return null
}

export const getPlazoCreditoLabel = (plazoRaw?: string | number | null): string => {
  if (plazoRaw === undefined || plazoRaw === null || plazoRaw === '') return '—'
  const plazoStr = String(plazoRaw).trim()
  const match = plazosPago.find((p) => p.value === plazoStr)
  if (match) return match.label
  const days = Number(plazoStr)
  if (!Number.isNaN(days) && days > 0) return `${days} días`
  return plazoStr
}

/** Total del pedido: usar valor calculado en pantalla o total + envio del API. */
export const resolveCmdmPedidoTotalCop = (
  order: CmdmCreditDisplayOrder,
  pedidoTotalCop?: number | null
): number | null => {
  if (
    pedidoTotalCop !== undefined &&
    pedidoTotalCop !== null &&
    !Number.isNaN(pedidoTotalCop) &&
    pedidoTotalCop > 0
  ) {
    return Math.round(pedidoTotalCop)
  }
  const rootTotal = Number(order.total)
  const envio = Number(order.envio ?? 0)
  if (!Number.isNaN(rootTotal) && rootTotal > 0) {
    const env = Number.isNaN(envio) ? 0 : envio
    return Math.round(rootTotal + env)
  }
  return null
}

export const buildCmdmCreditDisplayInfo = (
  order: CmdmCreditDisplayOrder,
  pedidoTotalCop?: number | null
): CmdmCreditDisplayInfo => {
  const totalNum = resolveCmdmPedidoTotalCop(order, pedidoTotalCop)
  return {
    empresa: getCmdmEmpresaDisplayName(order),
    nit: formatCmdmNit(order.customer?.document),
    creditoOtorgado: formatCreditoOtorgadoCop(totalNum),
    plazoCredito: getPlazoCreditoLabel(getPlazoFromOrderPagos(order))
  }
}

export const loadCmdmCreditDisplayInfo = (
  order: CmdmCreditDisplayOrder,
  pedidoTotalCop?: number | null
): CmdmCreditDisplayInfo => buildCmdmCreditDisplayInfo(order, pedidoTotalCop)

export const isCmdmPaymentMethod = (value?: string | null): boolean =>
  (value ?? '').toString().trim().toLowerCase() === 'cmdm'
